import { Modal } from './ui/Modal';
import { Badge } from './ui/Badge';
import { Table, THead, Th, Td, Tr, TTotalRow, TableFooter } from './ui/Table';
import { EmptyState, ErrorState, LoadingState } from './ui/States';
import { useMrrMonthDetail } from '../hooks/useDashboard';
import type { MrrMovementItem, MrrMovementType } from '../api/dashboard';
import type {
  ContractMonthState,
  CustomerNames,
  MrrMonth,
  MrrMonthCustomer,
  OverrideMovementKind,
  OverrideMovementNote,
} from '../api/finance';
import { useMrrMonth } from '../hooks/useFinance';
import { formatMonthYear } from '../utils/dateFormat';
import { formatMoney } from '../utils/numberFormat';
import { planDisplayName } from '../pages/ProjectManagement/Finance/formatters';

const SYMBOL: Record<string, string> = { EUR: '€', USD: '$', GBP: '£' };

function formatCurrency(value: number, currency: string): string {
  return formatMoney(Math.round(value), SYMBOL[currency] ?? `${currency} `);
}

function formatMonthLabel(monthKey: string): string {
  const [year, month] = monthKey.split('-');
  const date = new Date(Number(year), Number(month) - 1);
  return formatMonthYear(date);
}

/**
 * A contract's state in the month shown, never today's: a contract that ran
 * all of August is "Running" in August even though it has ended since.
 * Running is the default and gets no pill.
 */
const STATE_BADGE: Record<ContractMonthState, { label: string; variant: 'default' | 'success' | 'warning' } | null> = {
  running: null,
  started: { label: 'Started', variant: 'success' },
  ending: { label: 'Ends this month', variant: 'warning' },
  renewed: { label: 'Renewed', variant: 'default' },
};

const MOVEMENT_LABEL: Record<MrrMovementType, string> = {
  new: 'New',
  expansion: 'Expansion',
  contraction: 'Contraction',
  churn: 'Churned',
};

const deltaClass = (value: number) =>
  value > 0 ? 'text-success-dark' : value < 0 ? 'text-error-dark' : 'text-text-tertiary';

/** The badge for an override-only movement; a real movement keeps its engine label. */
export const OVERRIDE_KIND_LABEL: Record<OverrideMovementKind, string> = {
  restored: 'Rate restored',
  ended: 'Override ended',
  started: 'Override started',
  changed: 'Override changed',
};

/**
 * The muted line under a movement that a reporting override from the MRR
 * sheet explains: which override went or came, and what Odoo's contracts did
 * over the same two months, so a reader can tell it from new business.
 */
export function OverrideMovementLine({ note, currency }: { note: OverrideMovementNote; currency: string }) {
  const odoo =
    note.odooFrom === note.odooTo
      ? `Odoo contracts unchanged at ${formatCurrency(note.odooTo, currency)}`
      : `Odoo contracts ${formatCurrency(note.odooFrom, currency)} → ${formatCurrency(note.odooTo, currency)}`;
  const what =
    note.fromNote && note.toNote
      ? `Was: ${note.fromNote}. Now: ${note.toNote}.`
      : note.fromNote
        ? `Override ended: ${note.fromNote}.`
        : `Override started: ${note.toNote ?? 'no note'}.`;
  return (
    <p className="text-body-xs text-text-tertiary">
      {what} {odoo}.
    </p>
  );
}

/** The override note for one movement, if any. Movements and notes both name the Odoo partner. */
export function overrideNoteFor(
  notes: OverrideMovementNote[] | undefined,
  month: string,
  companyName: string,
): OverrideMovementNote | undefined {
  return notes?.find((n) => n.month === month && n.customer === companyName);
}

function MonthMovements({
  movements,
  currency,
  month,
  overrideNotes,
  customerNames,
}: {
  movements: MrrMovementItem[];
  currency: string;
  month: string;
  overrideNotes?: OverrideMovementNote[];
  customerNames?: Record<string, CustomerNames>;
}) {
  if (movements.length === 0) return null;
  return (
    <div className="mb-5">
      <p className="text-body-sm font-medium text-text-secondary mb-1">Changes vs previous month</p>
      <div className="divide-y divide-border-light">
        {movements.map((m, idx) => {
          const note = overrideNoteFor(overrideNotes, month, m.companyName);
          return (
          <div key={idx} className="flex items-center justify-between gap-3 py-2 text-body-sm">
            <div className="min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <Badge>{note?.kind ? OVERRIDE_KIND_LABEL[note.kind] : MOVEMENT_LABEL[m.type]}</Badge>
                <span className="min-w-0 break-words text-text-primary">
                  {customerNames?.[m.companyName]?.name ?? m.companyName}
                </span>
              </div>
              {note && <OverrideMovementLine note={note} currency={currency} />}
            </div>
            <div className="flex items-center gap-3 flex-shrink-0 tabular-nums">
              {(m.type === 'expansion' || m.type === 'contraction') && (
                <span className="text-body-xs text-text-tertiary">
                  {formatCurrency(m.previousMrr, currency)} → {formatCurrency(m.currentMrr, currency)}
                </span>
              )}
              <span className={`font-medium ${deltaClass(m.delta)}`}>
                {m.delta > 0 ? '+' : ''}
                {formatCurrency(m.delta, currency)}
              </span>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

interface MrrMonthDetailModalProps {
  month: string | null;
  onClose: () => void;
  /** Movements a reporting override explains, from the subscriptions summary. */
  overrideNotes?: OverrideMovementNote[];
  /** Engine customer → the Customers tab's name. */
  customerNames?: Record<string, CustomerNames>;
}

/** The contracts behind a customer's row, each with its state that month. */
function ContractsCell({ row, data }: { row: MrrMonthCustomer; data: MrrMonth }) {
  return (
    <div className="space-y-0.5">
      {row.contracts.map((c) => {
        const badge = STATE_BADGE[c.state];
        const label = `${planDisplayName(c.plan)} · ${formatCurrency(c.mrr, data.currency)}`;
        return (
          <div key={c.ref} className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-body-xs text-text-secondary">
            {data.odooBaseUrl && c.odooSubscriptionId !== null ? (
              <a
                href={`${data.odooBaseUrl}/odoo/subscriptions/${c.odooSubscriptionId}`}
                target="_blank"
                rel="noreferrer"
                className="break-words hover:underline"
                title="Open in Odoo"
              >
                {label}
              </a>
            ) : (
              <span className="break-words">{label}</span>
            )}
            {badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
          </div>
        );
      })}
    </div>
  );
}

export function MrrMonthDetailModal({ month, onClose, overrideNotes, customerNames }: MrrMonthDetailModalProps) {
  // Movements come from the dashboard's month; the per-customer rows from the
  // finance month, which is built on the same engine and adds up to the same total.
  const movementsQuery = useMrrMonthDetail(month);
  const { data, isLoading, error, refetch } = useMrrMonth(month);
  const contractSum = data ? data.customers.reduce((sum, c) => sum + c.contractMrr, 0) : 0;
  const overrideRows = data ? data.customers.filter((c) => c.override) : [];
  const notCounted = data ? data.customers.filter((c) => c.mrr === 0) : [];

  return (
    <Modal
      isOpen={!!month}
      onClose={onClose}
      title={month ? formatMonthLabel(month) : ''}
      description={
        data
          ? `Total MRR ${formatCurrency(data.total, data.currency)} · ${data.payingCustomers} paying customers`
          : undefined
      }
      size="full"
    >
      {error ? (
        <ErrorState title="Couldn’t load this month" error={error} onRetry={() => refetch()} />
      ) : isLoading ? (
        <LoadingState rows={5} />
      ) : data && data.customers.length > 0 ? (
        <div>
          {movementsQuery.error ? (
            <p className="mb-4 text-body-xs text-error">Couldn’t load this month’s changes.</p>
          ) : (
            <MonthMovements
              movements={movementsQuery.data?.detail.movements ?? []}
              currency={data.currency}
              month={data.month}
              overrideNotes={overrideNotes}
              customerNames={customerNames}
            />
          )}
          {/* Three columns so it fits the modal with no sideways scroll: each contract carries its Odoo
              amount, and the MRR cell names the Odoo total only where an override makes them differ. */}
          <Table colWidths={['34%', '46%', '20%']}>
            <THead>
              <tr>
                <Th>Customer</Th>
                <Th>Odoo contracts that month</Th>
                <Th align="right">MRR</Th>
              </tr>
            </THead>
            <tbody>
              {data.customers.map((row) => (
                <Tr key={row.customer}>
                  <Td className="font-medium">
                    <span className="block break-words text-text-primary">{row.name}</span>
                    {row.odooName && (
                      <span className="block break-words text-body-xs font-normal text-text-tertiary">{row.odooName}</span>
                    )}
                    {row.override && (
                      <span className="block text-body-xs font-normal text-text-tertiary">Override: {row.override.note}</span>
                    )}
                  </Td>
                  <Td>
                    <ContractsCell row={row} data={data} />
                  </Td>
                  <Td align="right" className="tabular-nums">
                    <span className="block font-medium">{formatCurrency(row.mrr, data.currency)}</span>
                    {Math.abs(row.mrr - row.contractMrr) >= 0.5 && (
                      <span className="block text-body-xs text-text-tertiary">
                        Odoo {formatCurrency(row.contractMrr, data.currency)}
                      </span>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
            <tfoot>
              <Tr>
                <Td colSpan={2} className="text-text-secondary">
                  Odoo contracts counted (renewals once)
                </Td>
                <Td align="right" className="tabular-nums text-text-secondary">
                  {formatCurrency(contractSum, data.currency)}
                </Td>
              </Tr>
              {Math.abs(data.overridesTotal) >= 0.5 && (
                <Tr>
                  <Td colSpan={2} className="text-text-secondary">
                    Reporting overrides from the MRR sheet ({overrideRows.map((r) => r.name).join(', ')})
                  </Td>
                  <Td align="right" className="tabular-nums text-text-secondary">
                    {data.overridesTotal > 0 ? '+' : '−'}
                    {formatCurrency(Math.abs(data.overridesTotal), data.currency)}
                  </Td>
                </Tr>
              )}
              {Math.abs(data.unexplained) >= 0.5 && (
                <Tr>
                  <Td colSpan={2} className="text-error">Not explained by any override: the engine and this list disagree</Td>
                  <Td align="right" className="tabular-nums text-error">{formatCurrency(data.unexplained, data.currency)}</Td>
                </Tr>
              )}
              <TTotalRow>
                <Td colSpan={2}>MRR</Td>
                <Td align="right" className="tabular-nums">
                  {formatCurrency(data.total, data.currency)}
                </Td>
              </TTotalRow>
            </tfoot>
          </Table>
          <TableFooter>
            One row per customer, contracts with their state in {formatMonthLabel(data.month)}
            {notCounted.length > 0 &&
              ` · ${notCounted.length} with contracts but €0 counted (${notCounted.map((r) => r.name).join(', ')})`}
          </TableFooter>
        </div>
      ) : (
        <EmptyState title="No active subscriptions this month" compact />
      )}
    </Modal>
  );
}
